import { Fragment, type ReactNode } from "react";
import { Link } from "react-router-dom";

/**
 * Renders the small Markdown subset release notes actually use — `###`
 * headers, `- ` bullet lists (a bullet may run to several indented
 * paragraphs), paragraphs, and `[text](url)` / `**bold**` inline spans. Not a general Markdown renderer: release notes are
 * hand-written and reviewed in this repo, so the subset only needs to cover
 * what `releases/*.md` uses, not arbitrary input.
 */
export function ReleaseNotes({ body }: { body: string }) {
  return <div className="release-notes">{parse(body).map(renderNode)}</div>;
}

type NoteNode =
  | { kind: "heading"; text: string }
  | { kind: "list"; items: string[][] } // each item: its paragraphs
  | { kind: "paragraph"; text: string };

/**
 * Blocks are separated by blank lines, but a bullet can run to several
 * paragraphs: a block indented under a list continues the bullet above it
 * rather than starting a new paragraph, as in CommonMark. Without that, the
 * rest of the section after such a bullet collapses into one paragraph.
 */
function parse(body: string): NoteNode[] {
  const nodes: NoteNode[] = [];

  for (const block of body.split(/\r?\n\r?\n+/)) {
    if (!block.trim()) continue;
    const raw = block.split(/\r?\n/);
    const lines = raw.map((l) => l.trim());

    const last = nodes[nodes.length - 1];
    if (last?.kind === "list" && /^\s+\S/.test(raw[0] ?? "")) {
      // The paragraph runs until the next unindented bullet, if the block
      // goes on to one.
      const next = raw.findIndex((l) => l.startsWith("- "));
      const end = next === -1 ? lines.length : next;
      last.items[last.items.length - 1]?.push(lines.slice(0, end).join(" "));
      if (next !== -1) addItems(last.items, lines.slice(next));
      continue;
    }

    const heading = /^###\s+(.+)$/.exec(lines[0] ?? "");
    if (heading) {
      nodes.push({ kind: "heading", text: heading[1] ?? "" });
      // A heading isn't guaranteed to have a blank line after it before
      // more content in the same block — keep whatever follows rather
      // than silently discarding it.
      if (lines.length > 1) pushLines(nodes, lines.slice(1));
      continue;
    }

    pushLines(nodes, lines);
  }

  return nodes;
}

/** A block's lines, as a bullet list (soft-wrapped continuation lines folded
 * into the item above them) or, failing that, a paragraph. */
function pushLines(nodes: NoteNode[], lines: string[]): void {
  if (lines[0]?.startsWith("- ")) {
    const items: string[][] = [];
    addItems(items, lines);
    nodes.push({ kind: "list", items });
    return;
  }

  nodes.push({ kind: "paragraph", text: lines.join(" ") });
}

/** Bullet lines appended to a list, with soft-wrapped continuation lines
 * folded into the item above them. */
function addItems(items: string[][], lines: string[]): void {
  for (const line of lines) {
    if (line.startsWith("- ")) {
      items.push([line.slice(2)]);
    } else {
      const item = items[items.length - 1];
      if (item) item[item.length - 1] += ` ${line}`;
    }
  }
}

function renderNode(node: NoteNode, key: number): ReactNode {
  switch (node.kind) {
    case "heading":
      return <h4 key={key}>{node.text}</h4>;
    case "paragraph":
      return <p key={key}>{inline(node.text)}</p>;
    case "list":
      return (
        <ul key={key}>
          {node.items.map(([first, ...more], j) => (
            <li key={j}>
              {inline(first ?? "")}
              {more.map((para, k) => (
                <p key={k}>{inline(para)}</p>
              ))}
            </li>
          ))}
        </ul>
      );
  }
}

/** `[text](url)`, `**bold**`, and `` `code` `` — the only inline spans
 * release notes use. */
function inline(text: string): ReactNode {
  const parts = text.split(/(\[[^\]]+\]\([^)]+\)|\*\*[^*]+\*\*|`[^`]+`)/g);
  return (
    <>
      {parts.map((part, i) => {
        const link = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(part);
        if (link) {
          const [, label, href] = link;
          return href!.startsWith("/") ? (
            <Link key={i} to={href!}>
              {label}
            </Link>
          ) : (
            <a key={i} href={href}>
              {label}
            </a>
          );
        }

        const bold = /^\*\*([^*]+)\*\*$/.exec(part);
        if (bold) {
          return <strong key={i}>{bold[1]}</strong>;
        }

        const code = /^`([^`]+)`$/.exec(part);
        if (code) {
          return (
            <code className="mono" key={i}>
              {code[1]}
            </code>
          );
        }

        return <Fragment key={i}>{part}</Fragment>;
      })}
    </>
  );
}
